import React from "react"
import { useQueryClient } from "@tanstack/react-query"
import type {
  DevToolsProgramSnapshot,
  DevToolsSubscriptionSnapshot,
  DevToolsSubscriptionStatusOverride,
  DevToolsUserSnapshot
} from "@internal/dev-tools"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { useMemberProgram, useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import { useIsPaywalled, useSubscriptionStatus } from "#/services/user/hooks/useSubscriptionStatus"
import {
  clearSubscriptionStatusOverride,
  forceSubscriptionStatus,
  useSubscriptionStatusOverride
} from "#/utils/devTools/subscriptionStatusOverride"
import { getAppEnv } from "#/utils/env"

/**
 * Maps the host's user context onto the shape the dev-tools package renders.
 *
 * The mapping lives here rather than in the package for two reasons: the package must not
 * depend on host types, and picking fields explicitly keeps the PII that reaches the page
 * reviewable in one place. Never spread the whole context — it carries the refresh/update
 * callbacks plus the full partner and insurance blocks.
 *
 * Safe to call unconditionally: it runs in production too (where the route that consumes it
 * is never registered) and just produces an object nothing reads.
 */
export function useDevToolsUserSnapshot(): DevToolsUserSnapshot {
  const currentUser = useCurrentUser()
  // Both hooks share the "GetMemberProgram" query key, so this is one request, not two — and
  // MainNavigation already issues it on every authenticated page, so the dev tools add none.
  // Reading the flags through useMemberProgramFlags keeps the isConsumer/isEngage derivation in
  // its one canonical place instead of re-comparing programType strings here.
  const programQuery = useMemberProgram()
  const programFlagsQuery = useMemberProgramFlags()

  const programStatus: DevToolsProgramSnapshot["status"] = programQuery.isLoading
    ? "loading"
    : programQuery.isError
    ? "error"
    : "ready"

  // ⚠ THE SAME LEVER THE ROUTE GATE PULLS, and it has to be. Both hooks below share
  // `["GetSubscriptionStatus"]` with PaywallGate's, and a query is enabled if ANY observer of
  // its key enables it — so a dev page that read the status while the gate's own read was
  // switched off would fill that cache and hand the gate a walling verdict it had deliberately
  // not asked for. A diagnostic must not change the behaviour it reports on.
  const consumerRoutesEnabled = useConsumerRoutesEnabled()
  const isConsumer = programFlagsQuery.data?.isConsumer === true
  const subscriptionQuery = useSubscriptionStatus(consumerRoutesEnabled && isConsumer)
  // The gate's verdict rather than `status !== "active"` recomputed here: a page that re-derived
  // it could never show the two disagreeing, which is the one bug worth coming here to find.
  const { isPaywalled } = useIsPaywalled(consumerRoutesEnabled)

  // "Nobody asked" is its own answer, and distinct from both "not back yet" and the real status
  // `none` — an enterprise member and a flag-off deployment issue no request at all.
  const subscriptionLoadStatus: DevToolsSubscriptionSnapshot["status"] = !consumerRoutesEnabled
    ? "not-applicable"
    : programStatus !== "ready"
    ? programStatus
    : !isConsumer
    ? "not-applicable"
    : subscriptionQuery.isError
    ? "error"
    : subscriptionQuery.data === undefined
    ? "loading"
    : "ready"
  const isSubscriptionReady = subscriptionLoadStatus === "ready"

  // The buttons that force a status, and the two pieces of state that keep the rows above from
  // passing a forced value off as the member's own.
  //
  // ⚠ THE ONLY WRITE THIS PAGE MAKES TO THE GATE'S CACHE, and the reason it is safe is that
  // `onOverride` is unreachable until the section is `ready` — see UserDataPage, which disables
  // every button otherwise. `ready` means the gate is already reading this key for this member,
  // so the write changes the ANSWER it gets rather than manufacturing a question it never asked.
  const queryClient = useQueryClient()
  const override = useSubscriptionStatusOverride()
  // The package declares its own copy of the status union, so these two assignments are what
  // hold the copies together: the parameter type here must accept everything the package can
  // send, and `overriddenTo` below must accept everything the BFF's union can hold.
  const onOverride = React.useCallback(
    (status: DevToolsSubscriptionStatusOverride) => forceSubscriptionStatus(queryClient, status),
    [queryClient]
  )
  const onClearOverride = React.useCallback(() => clearSubscriptionStatusOverride(queryClient), [queryClient])

  return React.useMemo(
    () => ({
      appEnv: getAppEnv(),
      appVersion: __APP_VERSION__ || "local",

      carrotUserId: currentUser?.carrotUserId ?? null,
      employeeId: currentUser?.employeeId ?? null,
      memberId: currentUser?.memberId ?? null,

      email: currentUser?.email ?? null,
      preferredEmail: currentUser?.preferredEmail ?? null,
      locale: currentUser?.locale ?? null,

      isTestUser: currentUser?.isTestUser ?? null,
      isPosingAsMember: currentUser?.isPosingAsMember ?? null,
      isInAppLockdown: currentUser?.isInAppLockdown ?? null,

      carrotPlansRoutingCompleted: currentUser?.carrotPlansRoutingCompleted ?? null,
      benefitEnrollmentWizardCompleted: currentUser?.benefitEnrollmentWizardCompleted ?? null,

      company: currentUser?.company
        ? {
            companyId: currentUser.company.companyId ?? null,
            companyName: currentUser.company.companyName ?? null,
            parentCompanyId: currentUser.company.parentCompanyId ?? null,
            countryCode: currentUser.company.countryCode ?? null
          }
        : null,

      program: {
        status: programStatus,
        programType: programQuery.data?.programType ?? null,
        isCarrotLite: programQuery.data?.isCarrotLite ?? null,
        isConsumer: programFlagsQuery.data?.isConsumer ?? null,
        isEngage: programFlagsQuery.data?.isEngage ?? null
      },

      subscription: {
        status: subscriptionLoadStatus,
        subscriptionStatus: isSubscriptionReady ? subscriptionQuery.data?.status ?? null : null,
        isPaywalled: isSubscriptionReady ? isPaywalled : null,
        overriddenTo: override?.forced ?? null,
        statusBeforeOverride: override?.realStatus ?? null,
        onOverride,
        onClearOverride
      }
    }),
    [
      currentUser,
      programStatus,
      programQuery.data,
      programFlagsQuery.data,
      subscriptionLoadStatus,
      isSubscriptionReady,
      subscriptionQuery.data,
      isPaywalled,
      override,
      onOverride,
      onClearOverride
    ]
  )
}
